import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import OptionallyVisible from 'components/optionallyVisible';

import { APIError } from 'types/error';

import { LABELS } from './keys';
import { CustomerSection } from './sections/customer/customer';
import { OrderSection } from './sections/order/order';
import { OrderStatusHistorySection } from './sections/orderStatusHistory/orderStatusHistory';
import { OrderSummarySection } from './sections/orderSummary/orderSummary';
import { TopAreaSection } from './sections/topArea/topArea';
import { TransactionsListSection } from './sections/transactionsList/transactionsList';
import { Container, SubContainer } from './styles';
import { OrderPwcData } from './types';

export interface OrderDetailsProps {
  orderPwcData: OrderPwcData;
  shouldShowExchangeDetailsSection: boolean;
  hasExtendedAccess: boolean;
  shouldShowSettlementDetailsSection: boolean;
  shouldShowOrderDetailsSection: boolean;
  shouldShowChargesListSection: boolean;
  shouldShowOrderStatusHistorySection: boolean;
  isS4cUser: boolean;
  isSupport: boolean;
  isPartnerAdmin: boolean;
  isIndividualMerchant: boolean;
  isMerchantResolved: boolean;
  isRefundMerchantCurrencySupported: boolean;
  unlock: {
    trigger: () => Promise<{ error?: APIError }>;
    isLoading: boolean;
  };
  cancel: {
    trigger: () => Promise<{ error?: APIError }>;
    isLoading: boolean;
  };
  cancelRefund: {
    trigger: (orderUuid: string, refundUuid: string) => Promise<{ error?: APIError }>;
    isLoading: boolean;
  };
}

export const OrderDetails = ({
  orderPwcData,
  shouldShowExchangeDetailsSection,
  hasExtendedAccess,
  shouldShowSettlementDetailsSection,
  shouldShowOrderDetailsSection,
  shouldShowChargesListSection,
  shouldShowOrderStatusHistorySection,
  isS4cUser,
  isSupport,
  isPartnerAdmin,
  isIndividualMerchant,
  isMerchantResolved,
  isRefundMerchantCurrencySupported,
  unlock,
  cancel,
  cancelRefund,
}: OrderDetailsProps) => {
  const sectionsProps = {
    orderData: orderPwcData,
  };

  const { t } = useTranslation();

  return (
    <>
      <TopAreaSection {...sectionsProps} />
      <Container>
        <SubContainer>
          <OrderSection
            {...sectionsProps}
            unlock={unlock}
            cancel={cancel}
            isS4cUser={isS4cUser}
            isPartnerAdmin={isPartnerAdmin}
            isIndividualMerchant={isIndividualMerchant}
            isMerchantResolved={isMerchantResolved}
            isRefundMerchantCurrencySupported={isRefundMerchantCurrencySupported}
            isSupport={isSupport}
          />
        </SubContainer>

        <SubContainer>
          <CustomerSection {...sectionsProps} showCustomerLink={hasExtendedAccess} />
          <OptionallyVisible visible={shouldShowOrderDetailsSection}>
            <OrderSummarySection {...sectionsProps} />
          </OptionallyVisible>

          <OptionallyVisible visible={shouldShowOrderStatusHistorySection}>
            <OrderStatusHistorySection {...sectionsProps} />
          </OptionallyVisible>
        </SubContainer>
      </Container>

      <OptionallyVisible visible={shouldShowChargesListSection}>
        <Panel label={t(LABELS.SECTIONS.CHARGES_LIST.TITLE)}>
          <TransactionsListSection
            orderPwcData={orderPwcData}
            shouldShowExchangeDetailsSection={shouldShowExchangeDetailsSection}
            shouldShowSettlementDetailsSection={shouldShowSettlementDetailsSection}
            isS4cUser={isS4cUser}
            cancelRefund={cancelRefund}
          />
        </Panel>
      </OptionallyVisible>
    </>
  );
};
